<template>
    <div class="payment-plan-panel" v-if="visiblePlans.length">
        <section-header title="Payment Plans" icon="fa-money-check-alt" :to="{name: 'account', query: {section: 'billing'}}" link-text="Account"/>

        <ui-card>
            <div v-for="(plan, idx) in visiblePlans" :key="plan.id" :class="['plan', {'mt-4 pt-4 plan-divider': idx > 0}]">
                <div class="plan-head">
                    <div class="plan-name">{{ plan.nickname || 'Payment Plan' }}</div>
                    <status-chip :label="status(plan).label" :type="status(plan).type"/>
                </div>
                <template v-if="plan.is_tpo">
                    <b-progress :type="paidPct(plan) >= 100 ? 'is-success' : 'is-info'" size="" :value="paidPct(plan)" class="enroll-progress"/>
                    <div class="plan-line">
                        <strong>{{ plan.paid | currency }}</strong> paid of <strong>{{ plan.total | currency }}</strong>
                        <span v-if="parseFloat(plan.monthly) > 0"> &middot; Monthly payment: <strong>{{ plan.monthly | currency }}</strong></span>
                    </div>
                    <div class="plan-line has-text-grey" v-if="plan.defer_on || plan.term">
                        <span v-if="plan.defer_on"><i class="far fa-calendar mr-1"></i>{{ status(plan).key === 'deferred' ? 'First payment' : 'Started' }} {{ plan.defer_on | dateOnly('MMM D, YYYY') }}</span>
                        <span v-if="plan.defer_on && plan.term"> &middot; </span>
                        <span v-if="plan.term">{{ plan.term }}-month term</span>
                    </div>
                </template>
                <div class="plan-line has-text-grey" v-else>
                    Legacy payment plan &mdash; for questions, contact <a href="mailto:support@my247ed.com">support@my247ed.com</a>.
                </div>
                <div class="plan-agreement mt-2" v-if="status(plan).key === 'onboarding' || status(plan).key === 'agreement_pending'">
                    <a :href="agreementUrl(plan) || tpoUrl" target="_blank" rel="noopener"><i class="far fa-file-signature mr-2"></i>Complete your agreement<i class="far fa-arrow-up-right-from-square ml-2"></i></a>
                </div>
            </div>

            <template #footer>
                <p class="caption">{{ caption }}</p>
                <div class="has-text-right mt-3" v-if="hasTpo">
                    <b-button tag="a" :href="tpoUrl" target="_blank" rel="noopener" type="is-primary" size="is-small"><i class="fas fa-up-right-from-square mr-2"></i>Access Account</b-button>
                </div>
            </template>
        </ui-card>
    </div>
</template>

<script>
    import {mapGetters} from "vuex";
    import SectionHeader from "../framework/SectionHeader";
    import StatusChip from "../framework/StatusChip";
    import UiCard from "../UiCard";
    import {planStatus, tpoAgreementUrl, TPO_CAPTION, TPO_URL} from "../../utils/paymentPlans";

    /**
     * Deliberately light: plans are serviced by The Payment Option, a
     * separate company. We show a coarse status, the paid/total/monthly
     * figures straight off the plan row, and hand off to TPO for
     * everything else. No schedules, history or payment-issue detail here.
     */
    export default {
        name: "PaymentPlanPanel",
        components: {SectionHeader, StatusChip, UiCard},
        data() {
            return {caption: TPO_CAPTION, tpoUrl: TPO_URL};
        },
        computed: {
            ...mapGetters(['paymentPlans']),
            // Paid-off plans are history — they stay on the Account page
            // but have no business on the dashboard (Derek 2026-09-02)
            visiblePlans() {
                // Paid-off and cancelled plans are history, not dashboard material
                return this.paymentPlans.filter(p => !['paid_off', 'cancelled'].includes(planStatus(p).key));
            },
            hasTpo() {
                return this.visiblePlans.some(p => p.is_tpo);
            }
        },
        methods: {
            status: planStatus,
            agreementUrl: tpoAgreementUrl,
            paidPct(plan) {
                const total = parseFloat(plan.total) || 0;
                if (!total) return 0;
                return Math.max(0, Math.min(100, (parseFloat(plan.paid) || 0) / total * 100));
            }
        }
    };
</script>

<style lang="scss" scoped>
    .plan-divider {
        border-top: 1px solid #ececec;
    }

    .plan-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        margin-bottom: 0.4rem;
    }

    .plan-name {
        font-weight: 700;
    }

    .plan-line {
        font-size: 0.9rem;
    }

    .plan-agreement {
        font-size: 0.85rem;
    }

    .caption {
        font-size: 0.75rem;
        color: #7a7a7a;
        line-height: 1.4;
    }
</style>
